import { ScrollableRegion } from '@syncanix/ui';

import { INLINE_LINK } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';
import { LegalPage } from '@/features/legal/LegalPage';
import { zdrAggregateKey } from '@/features/trust/content/provider-contracts';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import type { ReactElement } from 'react';

/**
 * Trust Center — privacy page (`/trust/privacy`). Phase 6 Pass 3
 * iter 247.
 *
 * Per `pass-3-trust-center.md` § Pages → `/trust/privacy`:
 * "Privacy Policy + per-data-type retention." Content authority:
 * `/product/trust/privacy.md` + `/product/trust/compliance.md`
 * § GDPR specifics.
 *
 * **Marketing voice (Marketer hat, locked)**: concrete numbers —
 * per-tier retention table (7 / 30 / 90 / 365 days + Enterprise
 * customer-configurable), 30-day erasure SLA, 24-hour
 * breach-notice SLA, specific GDPR Article references (15 / 16 /
 * 17 / 20 / 22 / 25 / 32 / 33).
 *
 * **Designer hat (locked)**: section-per-topic + `<table>` for the
 * retention matrix (gives procurement readers a clean copy-paste
 * artefact). Semantic `<section aria-labelledby>` + `<h2>` for AT.
 *
 * **Three-layer contract preserved**: pure presentational.
 *
 * Binding legal text: localized for convenience; the English version
 * governs. `<LegalTranslationNotice />` (self-hides on EN) states this
 * directly under the page `<h1>`.
 */
export function Privacy(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('privacy.seo.title'),
    description: t('privacy.seo.description'),
    path: '/trust/privacy',
  });
  return (
    <LegalPage
      breadcrumb={t('privacy.backToTrust')}
      title={t('privacy.title')}
      intro={t('privacy.intro')}
      lastUpdatedSlug="privacy"
    >
      <section aria-labelledby="priv-roles" className="mb-10">
        <h2
          id="priv-roles"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('privacy.roles.heading')}
        </h2>
        <p>
          {t('privacy.roles.body.1')}
          <strong>{t('privacy.roles.body.processor')}</strong>
          {t('privacy.roles.body.2')}
          <strong>{t('privacy.roles.body.controller')}</strong>
          {t('privacy.roles.body.3')}
        </p>
      </section>

      <section aria-labelledby="priv-defaults" className="mb-10">
        <h2
          id="priv-defaults"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('privacy.defaults.heading')}
        </h2>
        <ul className="list-disc space-y-1 ps-6">
          <li>
            <strong>{t('privacy.defaults.eu.lead')}</strong> {t('privacy.defaults.eu.body')}
          </li>
          <li>
            <strong>{t('privacy.defaults.noTraining.lead')}</strong>{' '}
            {t(zdrAggregateKey('privacy.defaults.noTraining.body'))}
          </li>
          <li>
            <strong>{t('privacy.defaults.minimisation.lead')}</strong>{' '}
            {t('privacy.defaults.minimisation.body')}
          </li>
        </ul>
      </section>

      <section aria-labelledby="priv-retention" className="mb-10">
        <h2
          id="priv-retention"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('privacy.retention.heading')}
        </h2>
        <p className="mb-3">{t('privacy.retention.body')}</p>
        <ScrollableRegion aria-label={t('privacy.retention.caption')} className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="caption-top mb-2 text-start text-sm text-[color:var(--bd-muted)]">
              {t('privacy.retention.caption')}
            </caption>
            <thead>
              <tr className="border-b border-[color:var(--bd-line)]">
                <th scope="col" className="py-2 pe-4 text-start font-semibold">
                  {t('privacy.retention.col.tier')}
                </th>
                <th scope="col" className="py-2 pe-4 text-start font-semibold">
                  {t('privacy.retention.col.conversation')}
                </th>
                <th scope="col" className="py-2 pe-4 text-start font-semibold">
                  {t('privacy.retention.col.audit')}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[color:var(--bd-line)]">
                <td className="py-2 pe-4 font-medium">{t('privacy.retention.tier.starter')}</td>
                <td className="py-2 pe-4">{t('privacy.retention.starter.conversation')}</td>
                <td className="py-2 pe-4">{t('privacy.retention.starter.audit')}</td>
              </tr>
              <tr className="border-b border-[color:var(--bd-line)]">
                <td className="py-2 pe-4 font-medium">{t('privacy.retention.tier.growth')}</td>
                <td className="py-2 pe-4">{t('privacy.retention.growth.conversation')}</td>
                <td className="py-2 pe-4">{t('privacy.retention.growth.audit')}</td>
              </tr>
              <tr className="border-b border-[color:var(--bd-line)]">
                <td className="py-2 pe-4 font-medium">{t('privacy.retention.tier.scale')}</td>
                <td className="py-2 pe-4">{t('privacy.retention.scale.conversation')}</td>
                <td className="py-2 pe-4">{t('privacy.retention.scale.audit')}</td>
              </tr>
              <tr>
                <td className="py-2 pe-4 font-medium">{t('privacy.retention.tier.enterprise')}</td>
                <td className="py-2 pe-4">{t('privacy.retention.enterprise.conversation')}</td>
                <td className="py-2 pe-4">{t('privacy.retention.enterprise.audit')}</td>
              </tr>
            </tbody>
          </table>
        </ScrollableRegion>
      </section>

      <section aria-labelledby="priv-gdpr" className="mb-10">
        <h2
          id="priv-gdpr"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('privacy.gdpr.heading')}
        </h2>
        <p className="mb-3">{t('privacy.gdpr.body')}</p>
        <ul className="list-disc space-y-2 ps-6">
          <li>
            <strong>{t('privacy.gdpr.art15.lead')}</strong> {t('privacy.gdpr.art15.body')}
          </li>
          <li>
            <strong>{t('privacy.gdpr.art16.lead')}</strong> {t('privacy.gdpr.art16.body')}
          </li>
          <li>
            <strong>{t('privacy.gdpr.art17.lead')}</strong> {t('privacy.gdpr.art17.body')}
          </li>
          <li>
            <strong>{t('privacy.gdpr.art20.lead')}</strong> {t('privacy.gdpr.art20.body')}
          </li>
          <li>
            <strong>{t('privacy.gdpr.art22.lead')}</strong> {t('privacy.gdpr.art22.body')}
          </li>
          <li>
            <strong>{t('privacy.gdpr.art25.lead')}</strong> {t('privacy.gdpr.art25.body')}
          </li>
          <li>
            <strong>{t('privacy.gdpr.art32.lead')}</strong> {t('privacy.gdpr.art32.body')}
          </li>
          <li>
            <strong>{t('privacy.gdpr.art33.lead')}</strong> {t('privacy.gdpr.art33.body')}
          </li>
        </ul>
      </section>

      {/* AUP (ADR-0432) — the pseudonymous visitor-profiles disclosure. Published
          truthfully as "when enabled": the feature is operator-controllable per
          workspace, and this section is the end-user-facing half of the DPIA § 9
          transparency ledger. */}
      <section aria-labelledby="priv-visitors" className="mb-10">
        <h2
          id="priv-visitors"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('privacy.visitors.heading')}
        </h2>
        <p className="mb-3">{t('privacy.visitors.body')}</p>
        <ul className="list-disc space-y-2 ps-6">
          <li>
            <strong>{t('privacy.visitors.identity.lead')}</strong>{' '}
            {t('privacy.visitors.identity.body')}
          </li>
          <li>
            <strong>{t('privacy.visitors.retention.lead')}</strong>{' '}
            {t('privacy.visitors.retention.body')}
          </li>
          <li>
            <strong>{t('privacy.visitors.erasure.lead')}</strong>{' '}
            {t('privacy.visitors.erasure.body')}
          </li>
          <li>
            <strong>{t('privacy.visitors.limits.lead')}</strong> {t('privacy.visitors.limits.body')}
          </li>
        </ul>
      </section>

      <section aria-labelledby="priv-pii" className="mb-10">
        <h2
          id="priv-pii"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('privacy.pii.heading')}
        </h2>
        <p className="mb-3">{t('privacy.pii.body')}</p>
        <ul className="list-disc space-y-1 ps-6">
          <li>
            <strong>{t('privacy.pii.field.lead')}</strong> {t('privacy.pii.field.body.1')}
            <code>pii:read</code>
            {t('privacy.pii.field.body.2')}
          </li>
          <li>
            <strong>{t('privacy.pii.freeText.lead')}</strong> {t('privacy.pii.freeText.body.1')}
            <code>[api-key]</code>, <code>[token]</code>
            {t('privacy.pii.freeText.body.2')}
          </li>
          <li>
            <strong>{t('privacy.pii.storage.lead')}</strong> {t('privacy.pii.storage.body.1')}
            <code>[email]</code>, <code>[phone]</code>
            {t('privacy.pii.storage.body.2')}
          </li>
        </ul>
      </section>

      {/*
        Z-8's binding condition (`plan/walk-first/zero-touch/DECISIONS.md`, and
        `SECURITY.md` § 3): "the storage-read exception is stated on the
        published trust page". SRD-48 residual 1 held it open — the promise
        published here was narrower than what a map does.

        It sits immediately before "Source-code privacy" because that section
        opens by naming this as the other discovery path; a reader who follows
        that sentence should already have read what this one does.
      */}
      <section aria-labelledby="priv-mapping" className="mb-10">
        <h2
          id="priv-mapping"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('privacy.mapping.heading')}
        </h2>
        <p className="mb-3">{t('privacy.mapping.body')}</p>
        <ul className="list-disc space-y-2 ps-6">
          <li>
            <strong>{t('privacy.mapping.match.lead')}</strong> {t('privacy.mapping.match.body')}
          </li>
          <li>
            <strong>{t('privacy.mapping.storage.lead')}</strong> {t('privacy.mapping.storage.body')}
          </li>
          <li>
            <strong>{t('privacy.mapping.values.lead')}</strong> {t('privacy.mapping.values.body')}
          </li>
          <li>
            <strong>{t('privacy.mapping.replay.lead')}</strong> {t('privacy.mapping.replay.body')}
          </li>
        </ul>
      </section>

      <section aria-labelledby="priv-source" className="mb-10">
        <h2
          id="priv-source"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('privacy.source.heading')}
        </h2>
        <p className="mb-3">{t('privacy.source.body1')}</p>
        <p className="mb-3">
          <strong>{t('privacy.source.body2.lead')}</strong> {t('privacy.source.body2.body')}
        </p>
        <p>
          <strong>{t('privacy.source.byok.lead')}</strong> {t('privacy.source.byok.body')}{' '}
          <code>.syncanixignore</code>
          {t('privacy.source.byok.ignore')} (<code>*.env</code>, <code>secrets/**</code>,{' '}
          <code>fixtures/**</code>, <code>__tests__/**</code>, <code>vendored/**</code>,{' '}
          <code>node_modules/**</code>).
        </p>
      </section>

      <section aria-labelledby="priv-us" className="mb-10">
        <h2
          id="priv-us"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('privacy.us.heading')}
        </h2>
        <p>
          {t('privacy.us.body.1')}
          <strong>{t('privacy.us.body.notSell')}</strong>
          {t('privacy.us.body.2')}
          <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
            admin@syncanix.com
          </a>
          {t('privacy.us.body.3')}
        </p>
      </section>

      <section aria-labelledby="priv-mena" className="mb-10">
        <h2
          id="priv-mena"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('privacy.mena.heading')}
        </h2>
        <p>{t('privacy.mena.body')}</p>
      </section>

      <section aria-labelledby="priv-isolation" className="mb-10">
        <h2
          id="priv-isolation"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('privacy.isolation.heading')}
        </h2>
        <p>{t('privacy.isolation.body')}</p>
      </section>

      <section aria-labelledby="priv-dsar" className="mb-10">
        <h2
          id="priv-dsar"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('privacy.dsar.heading')}
        </h2>
        <p>
          {t('privacy.dsar.body.1')}
          <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
            admin@syncanix.com
          </a>
          {t('privacy.dsar.body.3')}
        </p>
      </section>

      <section aria-labelledby="priv-related" className="mb-10">
        <h2
          id="priv-related"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('privacy.related.heading')}
        </h2>
        <ul className="list-disc space-y-1 ps-6">
          <li>
            <Link className={INLINE_LINK} to="/trust/subprocessors">
              {t('privacy.related.subprocessors.link')}
            </Link>{' '}
            {t('privacy.related.subprocessors.body')}
          </li>
          <li>
            <Link className={INLINE_LINK} to="/trust/security">
              {t('privacy.related.security.link')}
            </Link>{' '}
            {t('privacy.related.security.body')}
          </li>
          <li>
            <Link className={INLINE_LINK} to="/trust/compliance">
              {t('privacy.related.compliance.link')}
            </Link>{' '}
            {t('privacy.related.compliance.body')}
          </li>
        </ul>
      </section>

      <section aria-labelledby="priv-contact">
        <h2
          id="priv-contact"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('privacy.contact.heading')}
        </h2>
        <ul className="list-disc space-y-1 ps-6">
          <li>
            {t('privacy.contact.dsar')}{' '}
            <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
              admin@syncanix.com
            </a>
            {t('privacy.contact.period')}
          </li>
          <li>
            {t('privacy.contact.procurement')}{' '}
            <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
              admin@syncanix.com
            </a>
            {t('privacy.contact.period')}
          </li>
        </ul>
      </section>
    </LegalPage>
  );
}
